import {getTestProps} from '@benchling/js-test-utils';
import {Alert, Button, Chip, Group, Stack, Text} from '@mantine/core';
import React from 'react';

import {WORK_FOCUS_OPTIONS} from '../constants';
import type {SurveyAnswers} from '../ModelHubSignup';
import SignupStepHeader from '../SignupStepHeader';
import {
  SIGNUP_BUTTON_SIZE,
  SIGNUP_COMPACT_STACK_SPACING,
  SIGNUP_STEP_STACK_SPACING,
  useSignupStepStyles,
} from '../signupUi';

interface WorkStepProps {
  error: string | null;
  loading: boolean;
  survey: SurveyAnswers;
  onBack: () => void;
  onSubmit: () => void;
  onSurveyChange: (value: Partial<SurveyAnswers>) => void;
}

export default function WorkStep({
  error,
  loading,
  survey,
  onBack,
  onSubmit,
  onSurveyChange,
}: WorkStepProps): JSX.Element {
  const {classes} = useSignupStepStyles();
  // All survey answers are optional signal — the account is created regardless.
  const canSubmit = !loading;

  return (
    <Stack
      id="model-hub-signup-work-form"
      spacing={SIGNUP_STEP_STACK_SPACING}
      {...getTestProps({component: 'ModelHubSignup', element: 'workForm'})}
    >
      <SignupStepHeader title="Tell us about your work" onBack={onBack} />
      {error != null && (
        <Alert color="red" role="alert" title="Unable to create account">
          {error}
        </Alert>
      )}
      <Stack spacing={SIGNUP_COMPACT_STACK_SPACING}>
        <Text fw={600} size="lg">
          What do you work on?{' '}
          <Text span={true} color="dimmed" fw={400}>
            · select all that apply
          </Text>
        </Text>
        <Chip.Group
          multiple={true}
          value={survey.workFocus}
          onChange={(value) => {
            onSurveyChange({workFocus: value});
          }}
        >
          <Group spacing="md">
            {WORK_FOCUS_OPTIONS.map((option) => (
              <Chip
                key={option.value}
                classNames={{
                  iconWrapper: classes.workFocusChipIconWrapper,
                  label: classes.workFocusChipLabel,
                }}
                size="lg"
                value={option.value}
              >
                {option.label}
              </Chip>
            ))}
          </Group>
        </Chip.Group>
      </Stack>
      <Button
        disabled={!canSubmit}
        fullWidth={true}
        loading={loading}
        mt={SIGNUP_STEP_STACK_SPACING}
        size={SIGNUP_BUTTON_SIZE}
        onClick={onSubmit}
      >
        Create account
      </Button>
    </Stack>
  );
}
